<template>
  <div class="diag-builder-wrapper">
    <div
      v-if="isDataLoaded"
      class="my-container"
    >
      <item-panel></item-panel>
      <diagram
        :key="counter"
        @remove-leader-lines="removeLeaderLines($event)"
        @remove-all-leader-lines="removeAllLeaderLines"
        @add-leader-line="addLeaderLine($event)"
        :leaderLines="leaderLines"
      ></diagram>
      <detail-panel @remove-leader-lines="removeLeaderLines($event)" />
    </div>
    <div v-else class="diag-builder-loading">
      <b-spinner variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import db from '@/services/db'
import Icon from 'vue-awesome/components/Icon'
import { mapState, mapMutations, mapActions } from 'vuex'
import { applyWidgetsData } from '@/helpers/inherit-apply-methods'
import Diagram from './components/Diagram'
import DetailPanel from './components/DetailPanel'
import ToolBar from './components/ToolBar'
import ItemPanel from './components/ItemPanel'

export default {
  props: ['workflowKey'],
  components: {
    DetailPanel,
    diagram: Diagram,
    ToolBar,
    ItemPanel,
    icon: Icon,
  },
  computed: {
    ...mapState('diagramBuilder', [
      'nodes', 
      'lines',
      'currentNode',
      'counter',
    ]),
  },
  data() {
    return {
      isDataLoaded: false,
      leaderLines: [],
      widgetsData: {},
      templateDashboardsList: [],
      selectedTemplates: [],
      inheritedData: {},
    }
  },
  methods: {
    ...mapMutations('diagramBuilder', [
      'setWorkflow',
      'deleteLine',
      'setCurrentLines',
    ]),
    ...mapMutations(['setDashboardLoadingStatus', 'dataGrid_setOptions']),
    ...mapActions('diagramBuilder', ['getData']),
    addLeaderLine(line) {
      const that = this
      this.leaderLines.push(line)

      if (line.hasOwnProperty('anchor')) {
        document
          .getElementById(line.anchor)
          .addEventListener('contextmenu', function(event) {
            event.preventDefault()
            that.removeLeaderLines(line.id)
          })
      }
    },
    removeLeaderLines(input) {
      /* takes a single line ID or an array of line IDs to be deleted */
      let that = this
      let arr = []

      if (typeof input === 'string') {
        arr.push(input)
      } else if (Array.isArray(input)) {
        arr = input
      }

      arr.forEach(id => {
        this.leaderLines.forEach(leaderLine => {
          if (leaderLine.id === id) {
            leaderLine.remove()
            that.leaderLines = that.leaderLines.filter(
              _line => _line.id !== leaderLine.id
            )
            that.deleteLine(leaderLine.id)
          }
        })
      })
    },
    removeAllLeaderLines() {
      if (this.leaderLines.length > 0) {
        this.leaderLines.forEach(line => line.remove())
        this.leaderLines = []
      }
    },
  },
  async created() {
    this.setWorkflow(this.workflowKey)

    const getTemplateData = async () => {
      // get list of dashboards that marked as a template
      this.templateDashboardsList = await db.dashboards.getTemplateDashboardsList()
      this.selectedTemplates = await db.dashboards.getSelectedTemplates()
      const inheritedData = {}
      const templatesQueue = ['core']
        .concat(this.selectedTemplates)
        .map(template => {
          if (
            template !== 'core' &&
            !this.templateDashboardsList.includes(template)
          ) {
            console.error(
              `[Inherit Template] - template "${template}" doesn't exist in template list`
            )
            return
          }
          return {
            name: template,
            promise: new Promise(resolve => {
              db.root
                .ref('dashboards')
                .child(template)
                .once('value', snapshot => {
                  inheritedData[template] = snapshot.val()
                  for (let tabKey in inheritedData[template].tabs) {
                    inheritedData[template].tabs[tabKey].template = template
                  }
                  resolve()
                })
            }),
          }
        })
      await new Promise(resolve => {
        this.$loadingQueue(templatesQueue, () => {
          resolve()
        })
      })
      this.inheritedData = inheritedData
    }

    await getTemplateData()
    if (this.nodes.length === 0 && this.lines.length === 0) {
      await this.getData(this.workflowKey)
    }

    db.widgets.readData(widgetsData => {
      applyWidgetsData.bind(this)(widgetsData)
      this.dataGrid_setOptions(this.widgetsData.dataGrid || {})
      this.isDataLoaded = true
    })
  },
}
</script>

<style lang="scss">
@import './assets/scss/global';

.diag-builder-wrapper {
  height: calc(100vh - 107px);
}

.my-container {
  display: grid;
  grid-template-columns: 108px auto 300px;
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid hsl(0, 0%, 88%);
  padding: 0;
  margin: 0;
}

.diag-builder-loading {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>